import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MapPin, Calendar, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../../services/api';

function formatData(iso) {
  if (!iso) return '';
  // Coluna `date` do Postgres volta como timestamp ISO completo — slice pra pegar só a data.
  return new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Resumo dos horários do evento pra mostrar junto da data (a data do evento não tem hora —
// a hora fica em evento_horarios). 1 horário → "19:00"; vários → "19:00 / 20:00".
function resumoHorarios(horarios) {
  const hs = [...new Set((horarios || []).map(h => h.horario).filter(Boolean))].sort();
  return hs.join(' / ');
}

function nomeCompletoValido(nome) {
  const partes = (nome || '').trim().split(/\s+/).filter(Boolean);
  return partes.length >= 2 && partes.every(p => p.length >= 2);
}

function emailValido(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

function formatWhatsapp(value) {
  let digits = (value || '').replace(/\D/g, '');
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  return digits;
}

export default function ConvitePublico() {
  const { token } = useParams();
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [vinculo, setVinculo] = useState(''); // 'autonomo' | 'imobiliaria'
  const [imobiliaria, setImobiliaria] = useState('');
  const [erro, setErro] = useState('');
  const [concluido, setConcluido] = useState(null);
  const [preenchido, setPreenchido] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['convite-publico', token],
    queryFn: () => api.get(`/eventos/convite/${token}`).then(r => r.data.data),
    retry: false,
  });

  const evento = data?.evento;
  const convite = data?.convite;

  // Pré-preenche os campos com o snapshot do lead uma única vez (deixa o convidado editar depois)
  useEffect(() => {
    if (!convite || preenchido) return;
    setNome(convite.nome || '');
    setWhatsapp(formatWhatsapp(convite.whatsapp || ''));
    setEmail(convite.email || '');
    setVinculo(convite.vinculo_tipo || '');
    setImobiliaria(convite.imobiliaria_nome || '');
    setPreenchido(true);
    if (convite.status === 'confirmado') {
      setConcluido({ evento_nome: data.evento?.nome, data_inicio: data.evento?.data_inicio, local: data.evento?.local, jaConfirmado: true });
    }
  }, [convite, preenchido, data]);

  const confirmarMut = useMutation({
    mutationFn: () => api.post(`/eventos/convite/${token}/confirmar`, {
      nome: nome.trim(),
      whatsapp: whatsapp.trim(),
      email: email.trim(),
      vinculo_tipo: vinculo,
      imobiliaria_nome: vinculo === 'imobiliaria' ? imobiliaria.trim() : null,
    }),
    onSuccess: (res) => setConcluido(res.data.data),
    onError: (err) => setErro(err?.response?.data?.error || 'Não foi possível confirmar. Tente novamente.'),
  });

  const confirmar = () => {
    setErro('');
    if (!nome.trim() || !whatsapp.trim()) return setErro('Preencha nome e WhatsApp');
    if (!nomeCompletoValido(nome)) return setErro('Digite nome e sobrenome');
    if (!emailValido(email)) return setErro('Digite um e-mail válido');
    if (!vinculo) return setErro('Informe se você é corretor autônomo ou de imobiliária');
    if (vinculo === 'imobiliaria' && !imobiliaria.trim()) return setErro('Digite o nome da imobiliária');
    confirmarMut.mutate();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-md">
        {isLoading && <div className="text-center text-gray-400 py-16">Carregando...</div>}

        {isError && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <AlertCircle className="mx-auto text-red-400 mb-3" size={32} />
            <p className="text-gray-700 font-medium">Este convite não está mais disponível.</p>
          </div>
        )}

        {evento && !concluido && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {evento.imagem_url && (
              <img src={evento.imagem_url} alt={evento.nome} className="w-full max-h-72 object-cover" />
            )}
            <div className="bg-primary text-white px-6 py-5">
              <p className="text-xs uppercase tracking-wide text-white/70">Você foi convidado(a) para</p>
              <h1 className="text-lg font-bold">{evento.nome}</h1>
              <p className="text-sm text-white/80 flex items-center gap-1 mt-1"><Calendar size={13} /> {formatData(evento.data_inicio)}</p>
              {resumoHorarios(evento.horarios) && (
                <p className="text-sm text-white/80 flex items-center gap-1 mt-0.5"><Clock size={13} /> {resumoHorarios(evento.horarios)}</p>
              )}
              {evento.local && <p className="text-sm text-white/80 flex items-center gap-1 mt-0.5"><MapPin size={13} /> {evento.local}</p>}
            </div>

            <div className="p-6 space-y-4">
              {evento.descricao && <p className="text-sm text-gray-600 whitespace-pre-line">{evento.descricao}</p>}

              <p className="text-sm text-gray-500">Confira seus dados abaixo, corrija se precisar e confirme sua presença.</p>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome e sobrenome</label>
                <input className="input-field" placeholder="ex: João Silva" value={nome} onChange={e => setNome(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label>
                <input className="input-field" placeholder="(44) 99999-9999" value={whatsapp} onChange={e => setWhatsapp(formatWhatsapp(e.target.value))} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">E-mail</label>
                <input className="input-field" type="email" inputMode="email" autoCapitalize="none" placeholder="ex: joao@email.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Você é corretor(a)...</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setVinculo('autonomo')}
                    className={`py-2 rounded-lg text-sm font-medium border ${vinculo === 'autonomo' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    Autônomo
                  </button>
                  <button
                    type="button"
                    onClick={() => setVinculo('imobiliaria')}
                    className={`py-2 rounded-lg text-sm font-medium border ${vinculo === 'imobiliaria' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    De imobiliária
                  </button>
                </div>
              </div>
              {vinculo === 'imobiliaria' && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Qual imobiliária?</label>
                  <input className="input-field" placeholder="Digite o nome da imobiliária" value={imobiliaria} onChange={e => setImobiliaria(e.target.value)} />
                </div>
              )}

              {erro && <p className="text-sm text-red-600">{erro}</p>}

              <button
                onClick={confirmar}
                disabled={confirmarMut.isPending}
                className="btn-primary w-full py-3 text-base disabled:opacity-50"
              >
                {confirmarMut.isPending ? 'Confirmando...' : 'Confirmar presença de convidado'}
              </button>
            </div>
          </div>
        )}

        {concluido && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <CheckCircle2 className="mx-auto text-green-500 mb-3" size={40} />
            <h2 className="text-lg font-bold text-gray-800">
              {concluido.jaConfirmado ? 'Presença já confirmada' : 'Presença confirmada!'}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              Você é convidado(a) confirmado(a) do «{concluido.evento_nome || evento?.nome}»
              {concluido.data_inicio ? ` — ${formatData(concluido.data_inicio)}` : ''}
              {resumoHorarios(evento?.horarios) ? ` às ${resumoHorarios(evento?.horarios)}` : ''}
              {concluido.local ? `, ${concluido.local}` : ''}.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {concluido.jaConfirmado
                ? 'O QR Code de check-in já foi enviado no seu WhatsApp — apresente ele na chegada.'
                : 'Você vai receber a confirmação com o QR Code no seu WhatsApp — apresente ele na chegada.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
